/**
 * Farm Map API Service
 */
import apiClient from './apiClient';

export interface SectorCropInfo {
  species: string;
  variety: string;
}

export interface MapSector {
  id: number;
  name: string;
  field_id: string | null;
  field_name: string | null;
  irrigation_type: string | null;
  boundary: string; // GeoJSON string
  area_hectares: number | null;
  depletion_percent: number | null;
  depletion_mm: number | null;
  ks: number | null;
  etc_mm: number | null;
  wb_date: string | null;
  crop: SectorCropInfo | null;
}

export interface MapDeviceValve {
  id: string;
  name: string;
  valve_number: number;
  /** Current state from device telemetry: 'open' | 'closed' | null */
  status?: string | null;
}

export interface MapDevice {
  id: number;
  name: string;
  dev_eui: string;
  device_type: string;
  latitude: number | null;
  longitude: number | null;
  status?: string;
  last_seen_at?: string | null;
  battery_level_percent?: number | null;
  valves: MapDeviceValve[];
  depth_labels?: number[] | null;
  irrigation_sector_id?: number | null;
}

export interface MapField {
  id: string;
  name: string;
  boundary: string; // GeoJSON string
  area_hectares: number | null;
  crop: SectorCropInfo | null;
  crop_species_id: number | null;
}

export interface FarmMapOverview {
  farm_id: string;
  sectors: MapSector[];
  fields: MapField[];
  devices: MapDevice[];
  bounds: [[number, number], [number, number]] | null;
}

export interface SectorIrrigationStatus {
  sector_id: number;
  /** `queued` = its run is live but this sector's step has not started.
   *  A sequential program spends most of its length there. */
  state:
    | 'irrigating'
    /** Not flow-confirmed, but its valve is open and its own line pressurised
     *  (UC511 transducer): watering, measured by pressure only. */
    | 'irrigating_pressure'
    | 'irrigating_theoretical'
    | 'queued'
    | 'idle'
    /** Its own valve reports CLOSED while its own meter still reads flow:
     *  the line draining, a valve that did not seat, or a leak. */
    | 'closed_with_flow'
    | 'unknown';
  // 'flow' = confirmed by the sector's own flow meter (the only path to solid
  // 'irrigating'); 'program'/'manual' now accompany the 'in theory' state.
  source: 'flow' | 'program' | 'manual' | null;
  program_name: string | null;
  run_id: number | null;
  step_order: number | null;
  started_at: string | null;
  /** When this sector's step is due, set only while `queued`. */
  starts_at?: string | null;
  has_valve: boolean;
  valve_status: string | null;
  /** Latest measured flow (L/min) when state is 'irrigating' or
   *  'closed_with_flow' via 'flow'. */
  flow_lpm: number | null;
  /** Line pressure (kPa) behind an 'irrigating_pressure' state. */
  pressure_kpa?: number | null;
  /**
   * The sector appears in an Opta head's config but its valve is a UC51x the
   * head cannot reach. `valve_status` is then the valve's own telemetry, never
   * the head's relay — a remote sector's relay is a structural `false`.
   */
  is_remote: boolean;
  /**
   * The head running a program over this sector right now, when there is one.
   * On a remote sector this is the half the valve's own card cannot know: the
   * pump may be loaded against a valve that is already shut.
   */
  governed_by_head: {
    device_id: number;
    device_name: string | null;
    program_name: string | null;
    remaining_s: number | null;
  } | null;
}

export interface IrrigationStatusResponse {
  farm_id: string;
  generated_at: string;
  sectors: SectorIrrigationStatus[];
}

export interface SectorCwsiStatus {
  sector_id: number;
  cwsi: number | null;
  date: string | null;
  n_points: number;
  n_sensors: number;
  dev_euis: string[];
}

export interface CwsiStatusResponse {
  farm_id: string;
  generated_at: string;
  window_local: string;
  sectors: SectorCwsiStatus[];
}

const farmMapService = {
  getFarmMapOverview: async (farmId: string): Promise<FarmMapOverview> => {
    return apiClient.get<FarmMapOverview>(`/farms/${farmId}/map/overview`);
  },
  getIrrigationStatus: async (farmId: string): Promise<IrrigationStatusResponse> => {
    return apiClient.get<IrrigationStatusResponse>(`/farms/${farmId}/map/irrigation-status`);
  },
  getCwsiStatus: async (farmId: string): Promise<CwsiStatusResponse> => {
    return apiClient.get<CwsiStatusResponse>(`/farms/${farmId}/map/cwsi-status`);
  },
};

export default farmMapService;
